'use client';

import { ReactNode } from 'react';
import StarBackground from './StarBackground';

// Shared layout shell that brings non-homepage routes up to the brand
// language used on /quiz/results: gradient background + low-opacity star
// pattern + max-w-6xl content column. Use this for all group flow, account,
// and results pages so they feel like the rest of Votely.
//
// Mirrors the wrapper from results-client.tsx:
//   min-h-screen bg-linear-to-b from-background to-primary/10 p-4 md:p-8
//   star background at opacity-20
//   max-w-6xl mx-auto relative z-10
//
// Pages that want the homepage's plain bg should not use this.

interface PageShellProps {
  children: ReactNode;
  /** Max content width. Default 6xl matches results page; sm pages can pass narrower. */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl';
  /** Star density. 70 is gentle; 110 matches the homepage. */
  starDensity?: number;
}

const WIDTH: Record<NonNullable<PageShellProps['maxWidth']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
};

export default function PageShell({
  children,
  maxWidth = '6xl',
  starDensity = 70,
}: PageShellProps) {
  return (
    <div className="min-h-screen bg-linear-to-b from-background to-primary/10 p-4 md:p-8 relative overflow-hidden">
      <StarBackground density={starDensity} biasTop={false} opacity={0.2} />
      <div className={`${WIDTH[maxWidth]} mx-auto relative z-10`}>
        {children}
      </div>
    </div>
  );
}
